<script lang="ts" setup>
import _ from 'lodash'
import { computed, type Ref, ref } from 'vue'

import { filterStructuredDocument } from '../../components/health-data/utils'
import dayjs from '../../misc/dayjs'
import type { DocumentSourceIdentifier, LabResult, NumericalLabResult } from '../../types/health-data'
import type { CategoricalLabResult } from '../../types/health-data'
import OcticonButton from '../OcticonButton.vue'

export interface LabResultsViewerProps {
  numericalLabResults: NumericalLabResult[]
  activeLabResultIds?: number[]
  categoricalLabResults: CategoricalLabResult[]
  activeCategoricalLabResultIds: number[]
}

const props = defineProps<LabResultsViewerProps>()

const emit = defineEmits<{
  (e: 'show-source', documentSource: DocumentSourceIdentifier): void
}>()

const filtering = ref(null) as Ref<string>
const filteringOptions = {
  threshold: 0.49,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: ['title', 'code', 'value', 'unit', 'conclusion'],
}
const displayedLabResults = computed(() => {
  const numericalResults = props.numericalLabResults.map((labResult) => ({
    ...labResult,
    type: 'normal',
    isActive: props.activeLabResultIds.includes(labResult.id),
  })) as LabResult[]
  const categoricalResults = props.categoricalLabResults.map((labResult) => ({
    ...labResult,
    type: 'categorical',
    isActive: props.activeCategoricalLabResultIds.includes(labResult.id),
  })) as LabResult[]
  const results = numericalResults.concat(categoricalResults)

  return filterStructuredDocument(results, filtering.value, filteringOptions).filter((result) => result)
})
const sortedLabResults = computed(() =>
  _.orderBy(displayedLabResults.value, ['title', 'resultDate'], ['asc', 'desc']),
)
</script>

<template>
  <div>
    <div class="card-body row">
      <h1 class="report-title col">
        Résultats de Biologie
      </h1>
      <div class="col-auto form-group">
        <input
          v-model="filtering"
          type="text"
          placeholder="Filtrage rapide"
          class="form-control col-auto"
        >
      </div>
    </div>
    <div class="card-body table-responsive">
      <table class="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Nom</th>
            <th>Code</th>
            <th>Valeur</th>
            <th>Unité</th>
            <th>Conclusion</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="labResult in sortedLabResults"
            :key="`lab-result-${labResult.id}`"
            :class="{ 'hinted-line-highlighted': labResult.isActive }"
          >
            <td>{{ labResult.creationDate ? dayjs(labResult.creationDate).format('L') : '-' }}</td>
            <td>{{ labResult.title || '' }}</td>
            <td>{{ labResult.code || '' }}</td>
            <td>{{ labResult.value || '' }}</td>
            <td>{{ labResult.unit || '' }}</td>
            <td>{{ labResult.conclusion || '' }}</td>
            <td>
              <Octicon-button
                v-if="labResult.documentSourceIdentifier"
                octicon-name="hubot"
                custom-class="icon-link"
                title="Voir le document d'origine"
                :width="20"
                :height="20"
                @click="emit('show-source', labResult.documentSourceIdentifier)"
              />
            </td>
          </tr>
          <tr v-if="!sortedLabResults.length">
            Aucun résultat de laboratoire ne correspond au filtre "{{
              filtering
            }}"
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
